/** Default US continental bounds — used as fallback when no driver data is available. */
export const DEFAULT_US_BOUNDS: google.maps.LatLngBoundsLiteral = {
  north: 50,
  south: 23,
  west: -125,
  east: -66,
};

// ───────────────────────────────────────────────────────────── //
// MAP STYLE MATRIX
//
// Four styles organized along two axes: mode (light / dark) × variant (regular / spotlight).
// Feature visibility (labels, road hierarchy, highway shields with route numbers) is
// unified across light and dark in the same variant — only color tokens differ.
// ───────────────────────────────────────────────────────────── //

// LIGHT MAP STYLES ─────────────────────────────────────────────────────────────--- //
export const lightMapStyles: google.maps.MapTypeStyle[] = [
  // Hide click clutter
  {
    featureType: 'administrative.neighborhood',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'poi',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'administrative.land_parcel',
    stylers: [{ visibility: 'off' }],
  },

  // Highway shields (route numbers, interstate badges) — visible
  {
    featureType: 'road.highway',
    elementType: 'labels.icon',
    stylers: [{ visibility: 'on' }],
  },
  {
    featureType: 'road.highway.controlled_access',
    elementType: 'labels.icon',
    stylers: [{ visibility: 'on' }],
  },

  // Base landscape — light neutral canvas
  {
    featureType: 'landscape',
    elementType: 'geometry',
    stylers: [{ color: '#f4f6f8' }],
  },
  {
    elementType: 'labels.text.fill',
    stylers: [{ color: '#5a5a5a' }],
  },
  {
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#ffffff' }],
  },

  // Natural terrain / vegetation — soft green tint
  {
    featureType: 'landscape.natural.terrain',
    elementType: 'geometry',
    stylers: [{ color: '#c5dcc5' }],
  },
  {
    featureType: 'landscape.natural.landcover',
    elementType: 'geometry',
    stylers: [{ color: '#d4ead4' }],
  },

  // Water — soft blue-teal
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#b0cdd5' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#ffffff' }],
  },

  // Parks, nature reserves, attractions, sports complexes — pale green
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#d8ecd8' }],
  },
  {
    featureType: 'poi.attraction',
    elementType: 'geometry',
    stylers: [{ color: '#d8ecd8' }],
  },
  {
    featureType: 'poi.sports_complex',
    elementType: 'geometry',
    stylers: [{ color: '#d8ecd8' }],
  },

  // Administrative — subdued labels
  {
    featureType: 'administrative.country',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#777777' }],
  },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#555555' }],
  },

  // Roads — light fills
  {
    featureType: 'road',
    elementType: 'geometry.fill',
    stylers: [{ color: '#ffffff' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#5a5a5a' }],
  },
  {
    featureType: 'road.arterial',
    elementType: 'geometry',
    stylers: [{ color: '#f5f5f5' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#ebebeb' }],
  },
  {
    featureType: 'road.highway.controlled_access',
    elementType: 'geometry',
    stylers: [{ color: '#dcdcdc' }],
  },
  {
    featureType: 'road.local',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#808080' }],
  },

  // Transit
  {
    featureType: 'transit',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#5a5a5a' }],
  },
];

// DARK MAP STYLES ─────────────────────────────────────────────────────────────--- //
export const darkMapStyles: google.maps.MapTypeStyle[] = [
  // Remove click clutter
  {
    featureType: 'administrative.neighborhood',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'poi',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'administrative.land_parcel',
    stylers: [{ visibility: 'off' }],
  },

  // Highway shields (route numbers, interstate badges) — visible
  {
    featureType: 'road.highway',
    elementType: 'labels.icon',
    stylers: [{ visibility: 'on' }],
  },
  {
    featureType: 'road.highway.controlled_access',
    elementType: 'labels.icon',
    stylers: [{ visibility: 'on' }],
  },

  // Base
  {
    elementType: 'geometry',
    stylers: [{ color: '#161616' }],
  },
  {
    elementType: 'labels.text.fill',
    stylers: [{ color: '#8a8a8a' }],
  },
  {
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#161616' }],
  },

  // Administrative
  {
    featureType: 'administrative',
    elementType: 'geometry',
    stylers: [{ color: '#757575' }],
  },
  {
    featureType: 'administrative.country',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#9e9e9e' }],
  },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#bdbdbd' }],
  },

  // POI
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#757575' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#080808' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#616161' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#1b1b1b' }],
  },

  // Roads — fills
  {
    featureType: 'road',
    elementType: 'geometry.fill',
    stylers: [{ color: '#2c2c2c' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#8a8a8a' }],
  },
  {
    featureType: 'road.arterial',
    elementType: 'geometry',
    stylers: [{ color: '#373737' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#3c3c3c' }],
  },
  {
    featureType: 'road.highway.controlled_access',
    elementType: 'geometry',
    stylers: [{ color: '#4e4e4e' }],
  },
  {
    featureType: 'road.local',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#616161' }],
  },

  // Transit
  {
    featureType: 'transit',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#757575' }],
  },

  // Water
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#080808' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#555555' }],
  },
];

// LIGHT SPOTLIGHT MAP STYLES ─────────────────────────────────────────────────────────────--- //
// Dims labels, roads, and other elements to make overlays more visible.
export const lightSpotlightMapStyles: google.maps.MapTypeStyle[] = [
  // Spread light mode properties
  ...lightMapStyles,

  // Dim elements to make overlays more visible.
  {
    elementType: 'labels.text.fill',
    stylers: [{ color: '#bbbbbb' }],
  },
  {
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#ffffff' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ saturation: -100 }, { lightness: 50 }],
  },
  {
    featureType: 'road',
    elementType: 'labels.icon',
    stylers: [{ visibility: 'on' }, { saturation: -100 }, { lightness: 75 }],
  },
  {
    featureType: 'transit',
    elementType: 'labels.icon',
    stylers: [{ visibility: 'on' }, { saturation: -100 }, { lightness: 75 }],
  },
];

// DARK SPOTLIGHT MAP STYLES ─────────────────────────────────────────────────────────────--- //
// Dims labels, roads, and other elements to make overlays more visible.
export const darkSpotlightMapStyles: google.maps.MapTypeStyle[] = [
  // Spread dark mode properties
  ...darkMapStyles,

  // Dim elements to make overlays more visible.
  {
    elementType: 'labels.text.fill',
    stylers: [{ color: '#555555' }],
  },
  {
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#161616' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ saturation: -100 }, { lightness: -50 }],
  },
  {
    featureType: 'road',
    elementType: 'labels.icon',
    stylers: [{ visibility: 'on' }, { saturation: -100 }, { lightness: -75 }],
  },
  {
    featureType: 'transit',
    elementType: 'labels.icon',
    stylers: [{ visibility: 'on' }, { saturation: -100 }, { lightness: -75 }],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// RESOLVER
// ─────────────────────────────────────────────────────────────────────────────

export type MapMode = 'light' | 'dark';

/** Resolves the 2×2 matrix of mode × spotlight to the concrete style array. */
export const getMapStyles = (mode: MapMode, spotlight: boolean): google.maps.MapTypeStyle[] => {
  if (mode === 'light') {
    return spotlight ? lightSpotlightMapStyles : lightMapStyles;
  }
  return spotlight ? darkSpotlightMapStyles : darkMapStyles;
};
